import { ActionTree, GetterTree, MutationTree, Module } from "vuex"
import { RootState } from "./index"
import VueI18n from "vue-i18n"
import { SearchStateFilter } from "@/types/admin-search"

enum SearchType {
    LOCATION = "location",
    DEVICE = "device",
    OEM_DEVICE = "oem-device",
    USER = "user",
}

export interface SearchState {
    selectedFilters: Record<string, boolean>
    filters: Record<SearchType, Record<string, SearchStateFilter>>
    query: string
    searchTerms: Record<SearchType, Record<string, string>>
}

const state: SearchState = {
    selectedFilters: {},
    filters: {
        location: {
            companyName: { label: "admin.content.location.company_name" },
            locationGroupName: {
                label: "admin.content.location.location_group_name",
            },
            locationName: { label: "admin.content.location.name", default: true },
            ownerEmail: { label: "admin.content.owner.email" },
            ownerFirstName: { label: "admin.content.owner.first_name" },
            ownerLastName: { label: "admin.content.owner.last_name" },
        },
        device: {
            categoryName: { label: "admin.content.controller.category_name" },
            controllerAlias: { label: "admin.content.controller.alias" },
            controllerUnitId: { label: "admin.content.controller.unit_id_formatted" },
            deviceName: {
                label: "admin.content.controller.device_name",
                default: true,
            },
            deviceType: { label: "admin.content.controller.device_type" },
            ownerEmail: { label: "admin.content.owner.email" },
            ownerFirstName: { label: "admin.content.owner.first_name" },
            ownerLastName: { label: "admin.content.owner.last_name" },
        },
        "oem-device": {
            companyName: { label: "admin.content.location.company_name" },
            ethernetIp: { label: "admin.content.ethernet_ip" },
            hwVersion: { label: "admin.content.hw_version" },
            locationName: { label: "Location Name", default: true },
            mobileIp: { label: "admin.content.mobile_ip" },
            openVpnIp: { label: "admin.content.open_vpn_ip" },
            osVersion: { label: "admin.content.os_version" },
            softwareVersion: { label: "admin.content.software_version" },
            unitId: { label: "admin.content.unit_id_formatted" },
            wirelessIp: { label: "admin.content.wireless_ip" },
            ownerEmail: { label: "admin.content.owner.email" },
            ownerFirstName: { label: "admin.content.owner.first_name" },
            ownerLastName: { label: "admin.content.owner.last_name" },
        },
        user: {
            companyName: { label: "admin.content.location.company_name" },
            countryName: { label: "admin.content.location.country_name" },
            email: { label: "admin.content.email", default: true },
            firstName: { label: "admin.content.first_name" },
            lastName: { label: "admin.content.last_name" },
            // role: { label: "Role" },
        },
    },
    query: "",
    searchTerms: {
        location: {},
        device: {},
        "oem-device": {},
        user: {},
    },
}

const mutations: MutationTree<SearchState> = {
    setSelectedFilters(state, selectedFilters) {
        state.selectedFilters = selectedFilters
    },
    setQuery(state, query) {
        state.query = query
    },
    toggleFilter(state, key: string) {
        const selectedFiltersCount = Object.values(state.selectedFilters).filter(Boolean).length

        if (state.selectedFilters[key]) {
            if (selectedFiltersCount > 1) {
                state.selectedFilters[key] = false
            }
        } else {
            state.selectedFilters[key] = true
        }
    },
    setSearchTerms(
        state,
        payload: { searchType: SearchType; searchTerms: Record<string, string> },
    ) {
        state.searchTerms[payload.searchType] = payload.searchTerms
    },
    setSearchTerm(state, payload: { searchType: SearchType; key: string; value: string }) {
        state.searchTerms[payload.searchType][payload.key] = payload.value
    },
}

const actions: ActionTree<SearchState, RootState> = {
    initializeSelectedFilters({ commit, state }, searchType: SearchType) {
        const filters = state.filters[searchType]
        const selectedFilters = Object.keys(filters).reduce((acc, key) => {
            acc[key] = filters[key].default || false

            return acc
        }, {} as SearchState["selectedFilters"])

        commit("setSelectedFilters", selectedFilters)

        const searchTerms = Object.keys(filters).reduce((acc, key) => {
            acc[key] = ""

            return acc
        }, {} as Record<string, string>)

        commit("setSearchTerms", { searchType, searchTerms })
    },
    buildQuery({ state, commit }, searchType: SearchType) {
        const queryParams = new URLSearchParams()

        Object.keys(state.searchTerms[searchType]).forEach((key) => {
            const value = state.searchTerms[searchType][key]

            if (value) {
                queryParams.append(key, value)
            }
        })

        commit("setQuery", queryParams.toString())
    },
    toggleFilter({ commit }, key: string) {
        commit("toggleFilter", key)
    },
    setSearchTerm(
        { commit, dispatch },
        payload: { searchType: SearchType; key: string; value: string },
    ) {
        commit("setSearchTerm", payload)

        dispatch("buildQuery", payload.searchType)
    },
}

const getters: GetterTree<SearchState, RootState> = {
    getSelectedFilters(state) {
        return state.selectedFilters
    },
    getQuery(state) {
        return state.query
    },
    isFilterDisabled: (state) => (key: string) => {
        const selectedFiltersCount = Object.values(state.selectedFilters).filter(Boolean).length

        return selectedFiltersCount === 1 && state.selectedFilters[key]
    },
    getFilters: (state) => (searchType: SearchType, i18n: VueI18n) => {
        const filters = state.filters[searchType]
        const localizedFilters = Object.keys(filters).reduce((acc, key) => {
            acc[key] = { ...filters[key] }
            acc[key].label = i18n.t(acc[key].label)

            return acc
        }, {} as Record<string, SearchStateFilter>)

        return localizedFilters
    },
    getSearchTerms: (state) => (searchType: SearchType) => {
        return state.searchTerms[searchType]
    },
}

const searchModule: Module<SearchState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default searchModule
